import axios from "axios";
import { useState, useEffect, useMemo } from "react";
import { FaEdit, FaSave, FaTimesCircle, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";

const RecipeTable = ({ baseUrl, token, outlet, itemName }) => {
    const [recipeData, setRecipeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editedData, setEditedData] = useState(null);

    const kgVariations = useMemo(() => ['kg', 'kgs', 'kilogram', 'kilograms'], []);
    const gramVariations = useMemo(() => ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'], []);
    const literVariations = useMemo(() => ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'], []);
    const milliliterVariations = useMemo(() => ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls'], []);

    useEffect(() => {
        fetchRecipeData();
    }, []);

    const fetchRecipeData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/recipes`, {
                token: token,
                item_name: itemName,
                outlet: outlet
            });
            setRecipeData(response.data);
            setLoading(false);
        } catch (err) {
            setError("Error fetching data");
            setLoading(false);
        }
    };

    const handleEdit = (recipeId) => {
        setEditingId(recipeId);
        const recipeToEdit = recipeData.find(r => r.id === recipeId);
        setEditedData(JSON.parse(JSON.stringify(recipeToEdit)));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditedData(null);
    };

    const convertToBaseUnit = (quantity, uom, new_uom) => {
        const qty = parseFloat(quantity);
        if (isNaN(qty)) return 0;

        if (!new_uom || uom.toLowerCase() === new_uom.toLowerCase()) return qty;

        if (kgVariations.includes(uom.toLowerCase()) && gramVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000;
        }
        if (gramVariations.includes(uom.toLowerCase()) && kgVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000; 
        }

        if (literVariations.includes(uom.toLowerCase()) && milliliterVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000; 
        }
        if (milliliterVariations.includes(uom.toLowerCase()) && literVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000; 
        }

        return qty;
    };

    const convertToDisplayUnit = (quantity, uom, new_uom) => {
        const qty = parseFloat(quantity);
        if (isNaN(qty)) return 0;

        if (!new_uom || uom.toLowerCase() === new_uom.toLowerCase()) return qty;

        if (kgVariations.includes(uom.toLowerCase()) && gramVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000; 
        }
        if (gramVariations.includes(uom.toLowerCase()) && kgVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000; 
        }

        if (literVariations.includes(uom.toLowerCase()) && milliliterVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000; 
        }
        if (milliliterVariations.includes(uom.toLowerCase()) && literVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000; 
        }

        return qty;
    };

    const handleQuantityChange = (type, index, value) => {
        const newData = { ...editedData };

        if (type === 'item') {
            const item = { ...newData.items[index] };
            const displayQuantity = value;

            const baseQuantity = convertToBaseUnit(displayQuantity, item.uom, item.new_uom);

            item.display_quantity = displayQuantity;
            item.quantity = baseQuantity;

            const unit = parseFloat(item.unit) || 1;
            const rate = parseFloat(item.rate) || 0;
            item.cost = (baseQuantity / unit) * rate;

            newData.items[index] = item;
        } else if (type === 'sub_recipe') {
            const subRecipe = { ...newData.sub_recipes[index] };
            const displayQuantity = value;

            const baseQuantity = convertToBaseUnit(displayQuantity, subRecipe.uom, subRecipe.new_uom);

            subRecipe.display_quantity = displayQuantity;
            subRecipe.quantity = baseQuantity;

            const unit = parseFloat(subRecipe.unit) || 1;
            const rate = parseFloat(subRecipe.rate) || 0;
            subRecipe.costprice = (baseQuantity / unit) * rate;

            newData.sub_recipes[index] = subRecipe;
        }

        const itemsCost = newData.items.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
        const subRecipesCost = newData.sub_recipes?.reduce((sum, sub) => sum + (parseFloat(sub.costprice) || 0), 0) || 0;

        newData.costprice = itemsCost + subRecipesCost;

        const sellingPrice = parseFloat(newData.sellingprice) || 1;
        newData.cost_percent = (newData.costprice / sellingPrice) * 100;

        setEditedData(newData);
    };

    const handleSave = () => {
        if (!editedData) return;

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save changes?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss('save-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                const payload = {
                                    token: token,
                                    name: editedData.name,
                                    outlet: editedData.outlet,
                                    cost_price: editedData.costprice,
                                    selling_price: editedData.sellingprice,
                                    cost_percent: editedData.cost_percent,
                                    items: editedData.items.map(item => ({
                                        name: item.name,
                                        rate: item.rate,
                                        uom: item.uom,
                                        unit: item.unit,
                                        new_uom: item.new_uom || item.uom,
                                        quantity: item.quantity, 
                                        cost: item.cost
                                    })),
                                    sub_recipes: editedData.sub_recipes?.map(sub => ({
                                        sub_recipe_id: sub.sub_recipe_id,
                                        quantity: sub.quantity, 
                                        uom: sub.uom,
                                        unit: sub.unit,
                                        new_uom: sub.new_uom || sub.uom,
                                        costprice: sub.costprice,
                                        rate: sub.rate
                                    })) || []
                                };

                                await axios.put(`${baseUrl}/recipe/${editingId}`, payload);
                                await fetchRecipeData();
                                setEditingId(null);
                                setEditedData(null);
                                toast.success("Recipe updated successfully");
                            } catch (err) {
                                toast.error("Error saving recipe");
                                console.log(err);
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('save-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-recipe-changes'
            }
        );
    };

    const handleDelete = (recipeId) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/recipe/${recipeId}`, {
                                    data: { token }
                                });
                                await fetchRecipeData();
                                toast.success("Recipe deleted successfully")
                            } catch (err) {
                                console.log(err);
                                toast.error("Error deleting recipe")
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-recipe-confirmation'
            }
        );
    };

    if (loading) {
        return (
            <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return <div className="text-red-600 text-center">{error}</div>;
    }

    if (recipeData.length === 0) {
        return <div className="text-center mt-10 bg-gray-100 text-gray-700 font-medium p-4 space-y-2">
            <div>No recipe data available</div>
            <div>Do you want to <a href="/menuitemcost">add?</a></div>
        </div>
    }

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            {(editingId ? [editedData] : recipeData).map((recipe) => (
                <div key={recipe.id} className="mb-8">
                    <div className="flex justify-between items-center mt-4 mb-2">
                        <h2 className="text-xl font-bold">Ingredients</h2>
                        {editingId === recipe.id ? (
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleSave}
                                    className="h-8 px-3 border rounded flex items-center justify-center bg-green-600 text-white hover:bg-green-700"
                                >
                                    <FaSave className="h-4 w-4 mr-1" /> Save
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="h-8 px-3 border rounded flex items-center justify-center bg-red-600 text-white hover:bg-red-700"
                                >
                                    <FaTimesCircle className="h-4 w-4 mr-1" /> Cancel
                                </button>
                            </div>
                        ) : (
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleEdit(recipe.id)}
                                    className="h-8 px-3 border rounded flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    <FaEdit className="h-4 w-4 mr-1" /> Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(recipe.id)}
                                    className="h-8 px-3 border rounded flex items-center justify-center bg-red-600 text-white hover:bg-red-700"
                                >
                                    <FaTrashAlt className="h-4 w-4 mr-1" /> Delete
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="rounded-md border overflow-x-auto mb-6">
                        <table className="min-w-full divide-y divide-gray-200 text-center">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Ingredients</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Rate</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Unit</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Quantity</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">UOM</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {recipe.items.map((item, index) => (
                                    <tr key={`item-${index}`}>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-900">{item.name}</td>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-600">{item.rate}</td>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-600">{item.unit} ({item.uom})</td>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                            {editingId === recipe.id ? (
                                                <input
                                                    type="number"
                                                    step="any"
                                                    value={item.display_quantity || convertToDisplayUnit(
                                                        item.quantity,
                                                        item.uom,
                                                        item.new_uom
                                                    )}
                                                    onChange={(e) => handleQuantityChange('item', index, e.target.value)}
                                                    className="w-20 p-1 border rounded text-center"
                                                />
                                            ) : (
                                                convertToDisplayUnit(
                                                    item.quantity,
                                                    item.uom,
                                                    item.new_uom
                                                )
                                            )}
                                        </td>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-600">{item.new_uom || item.uom}</td>
                                        <td className="p-4 whitespace-nowrap text-sm text-gray-900">{Number(item.cost).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {recipe.sub_recipes && recipe.sub_recipes.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Sub-Recipes</h3>
                            <div className="rounded-md border overflow-x-auto mb-6">
                                <table className="min-w-full divide-y divide-gray-200 text-center">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Rate</th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Unit</th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Quantity</th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">UOM</th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {recipe.sub_recipes.map((subRecipe, index) => (
                                            <tr key={`sub-${index}`}>
                                                <td className="p-4 whitespace-nowrap text-sm font-medium text-gray-900">{subRecipe.name}</td>
                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">{subRecipe.rate}</td>
                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">{subRecipe.unit} ({subRecipe.uom})</td>
                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                    {editingId === recipe.id ? (
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            value={subRecipe.display_quantity || convertToDisplayUnit(
                                                                subRecipe.quantity,
                                                                subRecipe.uom,
                                                                subRecipe.new_uom
                                                            )}
                                                            onChange={(e) => handleQuantityChange('sub_recipe', index, e.target.value)}
                                                            className="w-20 p-1 border rounded text-center"
                                                        />
                                                    ) : (
                                                        convertToDisplayUnit(
                                                            subRecipe.quantity,
                                                            subRecipe.uom,
                                                            subRecipe.new_uom
                                                        )
                                                    )}
                                                </td>
                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">{subRecipe.new_uom || subRecipe.uom}</td>
                                                <td className="p-4 whitespace-nowrap text-sm text-gray-900">{Number(subRecipe.costprice)?.toFixed(2) || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="bg-gray-50 p-4 rounded-md">
                        <div className="grid grid-cols-3">
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Total Cost Price:</div>
                                <div className="font-semibold">{Number(recipe.costprice)?.toFixed(2)}</div>
                            </div>
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Selling Price:</div>
                                <div className="font-semibold">{Number(recipe.sellingprice)?.toFixed(2)}</div>
                            </div>
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Cost Percentage:</div>
                                <div className="font-semibold">{Number(recipe.cost_percent)?.toFixed(2)}%</div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default RecipeTable;